import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { entityService } from '@/services/EntityService';
import { isUuid } from '@equip/utils';

const normalizeEmail = (raw?: string | null): string | null => {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed || !trimmed.includes('@')) return null;
  return trimmed;
};

const fetchMemberEmail = async (memberId: string): Promise<string | null> => {
  const entity = await entityService.get({ type: 'person', id: memberId });
  const rawEmail =
    (entity?.entity_data?.email as string | undefined) ??
    (entity?.metadata?.email as string | undefined);
  return normalizeEmail(rawEmail);
};

/**
 * Resolve a member's email address with light caching via React Query.
 * Accepts an optional initialEmail that short-circuits the fetch when present.
 */
export const useMemberEmail = (memberId?: string | null, initialEmail?: string | null) => {
  const normalizedInitial = useMemo(() => normalizeEmail(initialEmail), [initialEmail]);

  const enabled = Boolean(memberId && isUuid(memberId) && !normalizedInitial);

  const query = useQuery({
    queryKey: ['member-email', memberId],
    queryFn: () => fetchMemberEmail(memberId as string),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const email = normalizedInitial ?? query.data ?? null;

  return {
    email,
    isLoading: query.isLoading || query.isFetching,
    error: enabled ? query.error : null,
  };
};
